import React from 'react';
import ReactDOM from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import OrderDetails from './OrderDetails';
import { StatusPill } from '../ui/gridCell';

/**
 * Shared order details modal used by Track By Customer / Track By Bill.
 */
const OrderDetailsModal = ({
  open,
  orderId,
  voucherNo,
  status,
  onClose,
  initialTab = 0,
}) => {
  const navigate = useNavigate();

  if (!open || !orderId) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Order details"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
        aria-label="Close dialog backdrop"
        onClick={onClose}
      />

      <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="h-1.5 w-full bg-gradient-to-r from-[#46955A] via-[#5aaf6e] to-[#46955A]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50/90 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-base font-semibold text-slate-900 sm:text-lg">
                {voucherNo ? `Order ${voucherNo}` : 'Order Details'}
              </h3>
              {status?.Name ? <StatusPill status={status} /> : null}
            </div>
            <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
              Same details as the full order page — switch tabs for history, pickup, delivery, and more.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/home/orders/${orderId}`);
              }}
              className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-white hover:text-[#46955A]"
              title="Open full page"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-white hover:text-slate-800"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-white px-2 py-2 sm:px-4 sm:py-3">
          <OrderDetails
            key={`${orderId}-${initialTab}`}
            orderId={String(orderId)}
            embedded
            initialTab={initialTab}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrderDetailsModal;
